import DOMPurify from "dompurify";
import type { GuideAudience, IGuideListItem } from "@/types/app-guides.interface";
import { MS_GUIDES_PATHS } from "@/routes/my-space/routes.guides";
import { COMPANY_GUIDES_PATHS } from "@/routes/company/routes.guides";

/**
 * Hosts an iframe may load. The API already builds `videoEmbedUrl` itself from
 * a YouTube / Vimeo id — this is the client's second check, so a bad row can
 * never turn into an arbitrary iframe.
 */
const EMBED_HOSTS = new Set([
  "www.youtube-nocookie.com",
  "player.vimeo.com",
  "embed.app.guidde.com",
]);

export const safeEmbedUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === "https:" && EMBED_HOSTS.has(u.hostname) ? u.toString() : null;
  } catch {
    return null;
  }
};

/** A playable <video> source: https only (uploads are served from our API). */
export const safeVideoSrc = (url: string | null | undefined): string | null => {
  if (!url) return null;
  try {
    return new URL(url).protocol === "https:" ? url : null;
  } catch {
    return null;
  }
};

/**
 * Guide HTML is sanitized on save; this is defence in depth before it reaches
 * `dangerouslySetInnerHTML`. Links open in a new tab, without an opener.
 */
export const sanitizeGuideHtml = (html: string | null | undefined): string => {
  const clean = DOMPurify.sanitize(html ?? "", {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["style", "iframe", "form", "input"],
    FORBID_ATTR: ["style"],
    RETURN_DOM_FRAGMENT: true,
  });
  clean.querySelectorAll("a[href]").forEach((a) => {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
  });
  const box = document.createElement("div");
  box.appendChild(clean);
  return box.innerHTML;
};

/** `m:ss` / `h:mm:ss` for a video length badge. */
export const formatDuration = (seconds: number | null | undefined): string | null => {
  if (!seconds || seconds < 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const pad = (n: number) => String(n).padStart(2, "0");
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

/** Which space a pathname belongs to — the same test the sidebar uses. */
export const audienceFromPath = (pathname: string): GuideAudience =>
  pathname === "/company" || pathname.startsWith("/company/") ? "workspace" : "my_space";

/** Guides library root for a space — the one source for menu, top bar and search. */
export const guidesRootFor = (space: "myspace" | "company"): string =>
  space === "company" ? COMPANY_GUIDES_PATHS.ROOT : MS_GUIDES_PATHS.ROOT;

export interface GuideGroup {
  key: string;
  /** Category id; null for the uncategorised group. */
  id: number | null;
  name: string;
  guides: IGuideListItem[];
}

/**
 * Group by category in the admin's order (then name), keeping the API's order
 * inside each (the admin's guide order); uncategorised guides go last as "General".
 */
export const groupByCategory = (guides: IGuideListItem[], generalLabel: string): GuideGroup[] => {
  const groups = new Map<string, GuideGroup & { order: number }>();
  for (const g of guides) {
    const key = g.category ? `c${g.category.id}` : "general";
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        id: g.category?.id ?? null,
        name: g.category?.name ?? generalLabel,
        order: g.category?.sortOrder ?? 0,
        guides: [],
      });
    }
    groups.get(key)!.guides.push(g);
  }
  return [...groups.values()]
    .sort((a, b) =>
      a.key === "general" ? 1 : b.key === "general" ? -1 : a.order - b.order || a.name.localeCompare(b.name),
    )
    .map((g) => ({ key: g.key, id: g.id, name: g.name, guides: g.guides }));
};

/**
 * Give each `<h2>` in (already sanitized) guide HTML an id and return them,
 * for the "In this guide" outline. Ids are prefixed so two guides on one page
 * never collide.
 */
export const withHeadingAnchors = (
  html: string,
  prefix: string,
): { html: string; headings: { id: string; text: string }[] } => {
  if (!html) return { html, headings: [] };
  const box = document.createElement("div");
  box.innerHTML = html;
  const headings = [...box.querySelectorAll("h2")].map((h, i) => {
    const id = `${prefix}-step-${i + 1}`;
    h.id = id;
    return { id, text: (h.textContent ?? "").trim() };
  });
  return { html: box.innerHTML, headings };
};

/** A fixed colour per module, so a module looks the same everywhere. Classes are literal for Tailwind. */
const TONES = [
  { solid: "bg-sky-500", soft: "bg-sky-50", text: "text-sky-700", ring: "border-sky-200" },
  { solid: "bg-emerald-500", soft: "bg-emerald-50", text: "text-emerald-700", ring: "border-emerald-200" },
  { solid: "bg-amber-500", soft: "bg-amber-50", text: "text-amber-700", ring: "border-amber-200" },
  { solid: "bg-violet-500", soft: "bg-violet-50", text: "text-violet-700", ring: "border-violet-200" },
  { solid: "bg-rose-500", soft: "bg-rose-50", text: "text-rose-700", ring: "border-rose-200" },
  { solid: "bg-teal-500", soft: "bg-teal-50", text: "text-teal-700", ring: "border-teal-200" },
  { solid: "bg-orange-500", soft: "bg-orange-50", text: "text-orange-700", ring: "border-orange-200" },
  { solid: "bg-indigo-500", soft: "bg-indigo-50", text: "text-indigo-700", ring: "border-indigo-200" },
] as const;
const GENERAL_TONE = { solid: "bg-slate-400", soft: "bg-slate-50", text: "text-slate-600", ring: "border-slate-200" };

export type ModuleTone = (typeof TONES)[number] | typeof GENERAL_TONE;

export const moduleTone = (moduleId: number | null | undefined): ModuleTone =>
  moduleId == null ? GENERAL_TONE : TONES[Math.abs(moduleId) % TONES.length];
